/**
 * AG Grid Column Builder for Carrier-Grouped (PPX) Benefit Plan Groups
 */

import {
  BaseBenefitPlanPolicyPeriod,
  BenefitPlanTypeEnum,
  CoverageTypeEnum,
  NetworkEnum,
} from "@platform/openapi-types";
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  DefaultMenuItem,
  GetMainMenuItems,
  MenuItemDef,
} from "ag-grid-community";
import {
  createBenefitPlanColumnsFromGroups,
  createBenefitsColumnWithHeaderLabels,
  createLabelledBenefitsColumn,
  extractColumnContext,
  formatPolicyPeriodDisplay,
  type PlanDocumentMap,
} from "@platform/plan-grid-core";

import CarrierGroupHeader from "../components/HeaderComponents/CarrierGroupHeader";
import VersionRowHeader from "../components/HeaderComponents/VersionRowHeader";
import { formatRequestedColumnLabel } from "../../../utilities/formatRequestedColumnLabel";

import {
  movePlanDestinations,
  type MovePlanDestinationPage,
} from "./movePlanDestinations";

import type { CarrierLaneContext } from "./gatherCarrierLanes";
import type { QuoteVersionState } from "../components/HeaderComponents/RequoteBadge";
import type {
  BenefitPlanGroup,
  RfpBenefitPlan,
  RfpCoverage,
} from "@platform/plan-grid-core";
import type { ColumnBuildStrategy } from "@platform/plan-grid-core";
import type {
  CarrierColumnGroup,
  PadFillTarget,
  PlaceholderMaterializeTarget,
  RequestedBenefitPlanGroup,
} from "../../../adapters/proposalToGrid";

/** The carrier's whole quote for the selected page a "Move Plan Group" action targets. */
export interface MovePlanTarget {
  groupId: string;
  productType: BenefitPlanTypeEnum;
}

/**
 * Config threaded into {@link buildCarrierGroupedColumns} so the carrier
 * header menu (CarrierGroupHeader) can offer "Move Plan Group" (btx-unmapped-plans
 * "Move Plan Group" relocation): `requestedPages` and `currentPageId` decide whether
 * the group has anywhere to go (see {@link movePlanDestinations}), `onMovePlan`
 * opens the move dialog.
 */
export interface MovePlanMenuConfig {
  /** Every requested plan group of the coverage, never the synthetic Unmapped Plans page. */
  requestedPages: readonly MovePlanDestinationPage[];
  /** The page on screen — a destination the group already sits on is no destination. */
  currentPageId?: string;
  onMovePlan: (target: MovePlanTarget) => void;
}

export interface PasteTarget {
  planGroupId: string;
  targetPosition: number;
}

/** The data copied off a plan's "Copy Plan" menu action, ready to paste elsewhere. */
export interface CopiedPlanDetails {
  carrierId: string;
  carrierName: string;
  planLabel: string;
  sourcePlanId: string;
  type: RfpBenefitPlan["type"];
  optionName: RfpBenefitPlan["optionName"];
  benefits: RfpBenefitPlan["benefits"];
  fundingType: RfpBenefitPlan["fundingType"];
  participationRequirements: RfpBenefitPlan["participationRequirements"];
  rateGuarantee: RfpBenefitPlan["rateGuarantee"];
  rateCap: RfpBenefitPlan["rateCap"];
  eligibilityClasses: RfpBenefitPlan["eligibilityClasses"];
}

// Mirrors the app-level DeletePlanInfo in usePlanMenuOptions.tsx: `kind`
// routes the delete dialog to the right endpoint depending on whether the
// plan is real, an awaiting-quote placeholder, or an empty pad slot on a
// real under-quoted group.
type DeletePlanInfo = (
  | { kind: "real"; id: string }
  | { kind: "placeholder"; target: PlaceholderMaterializeTarget }
  | { kind: "pad"; target: PadFillTarget }
) & {
  name: string;
  groupName: string;
};

/**
 * Grid-level variant of getNetworks for the carrier-grouped grid: the network
 * tier must be a single decision for the whole grid so every carrier builds
 * the same header depth (see buildCarrierGroupedColumns). Any dental PPO plan
 * group on any carrier turns the tier on for all of them.
 */
const getGridNetworks = (
  carrierGroups: CarrierColumnGroup[],
  coverage: RfpCoverage,
): readonly string[] => {
  if (coverage.coverageType !== CoverageTypeEnum.dental) {
    return [];
  }

  const anyPpoGroup = carrierGroups.some((c) =>
    c.benefitPlanGroups.some((g) => g.productType === BenefitPlanTypeEnum.ppo),
  );

  return anyPpoGroup ? [NetworkEnum.inNetwork, NetworkEnum.outOfNetwork] : [];
};

const DELETE_PLAN_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#d32f2f"><path d="M14.12 10.47 12 12.59l-2.13-2.12-1.41 1.41L10.59 14l-2.12 2.12 1.41 1.41L12 15.41l2.12 2.12 1.41-1.41L13.41 14l2.12-2.12zM15.5 4l-1-1h-5l-1 1H5v2h14V4zM6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM8 9h8v10H8V9z"/></svg>';
const COPY_PLAN_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"></path></svg>';

const CARRIER_HEADER_CLASS = "header-carrier-group";
// Highlights a carrier's header on the Unmapped Plans page (btx-unmapped-plans).
const UNMAPPED_CARRIER_HEADER_CLASS = "carrier-header--unmapped";
// Marks an incoming pending requote's column purple across the SBS grid (ADR-031).
const REQUOTE_PENDING_HEADER_CLASS = "carrier-header--pending-requote";
// Archived and deleted columns share one grey treatment — they are both out of play, and
// the version chip is what names which (ADR-031, CD-89).
const HISTORICAL_HEADER_CLASS = "carrier-header--historical";
// The "Version" tier between the Carrier row and the Plan tier (ADR-031, quote history).
const VERSION_HEADER_CLASS = "header-version-row";
const SOURCE_DOCUMENT_LABEL = "Source Document";

// Outer-edge outline classes for a pending requote column (ADR-031). Applied
// only along the leftmost/rightmost column path so the border traces the whole
// column's outside, never the seams between its In/Out network sub-columns.
const PENDING_EDGE_LEFT_HEADER_CLASS = "header-bpg--pending-edge-left";
const PENDING_EDGE_RIGHT_HEADER_CLASS = "header-bpg--pending-edge-right";
const PENDING_EDGE_LEFT_CELL_CLASS = "cell-bpg--pending-edge-left";
const PENDING_EDGE_RIGHT_CELL_CLASS = "cell-bpg--pending-edge-right";
const HISTORICAL_EDGE_LEFT_HEADER_CLASS = "header-bpg--historical-edge-left";
const HISTORICAL_EDGE_RIGHT_HEADER_CLASS = "header-bpg--historical-edge-right";
const HISTORICAL_EDGE_LEFT_CELL_CLASS = "cell-bpg--historical-edge-left";
const HISTORICAL_EDGE_RIGHT_CELL_CLASS = "cell-bpg--historical-edge-right";

/** Normalizes a resolved header/cell class value to an array and appends `className`. */
const withAppendedClass = (existing: unknown, className: string): string[] => {
  const classes = Array.isArray(existing)
    ? (existing as string[])
    : typeof existing === "string"
      ? [existing]
      : [];
  return [...classes, className];
};

/** Appends a header class, preserving any existing string/array value. */
const appendHeaderClass = (
  col: ColDef | ColGroupDef,
  className: string,
): void => {
  col.headerClass = withAppendedClass(col.headerClass, className);
};

/** Appends a cell class, wrapping any existing string/array/function value. */
const appendCellClass = (col: ColDef, className: string): void => {
  const original = col.cellClass;
  col.cellClass = (params: CellClassParams): string[] =>
    withAppendedClass(
      typeof original === "function" ? original(params) : original,
      className,
    );
};

/**
 * Tags the outermost column path with a version column's outline classes — the purple
 * pending family, or the grey historical one shared by archived and deleted, per
 * `historical`.
 *
 * Tags this node plus its first (edge = "left") or last (edge = "right") descendant at
 * every level, down to the leaf. Group cells span their whole subtree, so a border on the
 * leftmost/rightmost path draws the column's outer edge without touching the seams between
 * sibling sub-columns (e.g. In / Out network).
 */
const tagVersionColumnEdge = (
  node: ColDef | ColGroupDef,
  edge: "left" | "right",
  historical: boolean,
): void => {
  appendHeaderClass(
    node,
    edge === "left"
      ? historical
        ? HISTORICAL_EDGE_LEFT_HEADER_CLASS
        : PENDING_EDGE_LEFT_HEADER_CLASS
      : historical
        ? HISTORICAL_EDGE_RIGHT_HEADER_CLASS
        : PENDING_EDGE_RIGHT_HEADER_CLASS,
  );
  const children = (node as ColGroupDef).children;
  if (children && children.length > 0) {
    tagVersionColumnEdge(
      edge === "left" ? children[0] : children[children.length - 1],
      edge,
      historical,
    );
  } else {
    appendCellClass(
      node as ColDef,
      edge === "left"
        ? historical
          ? HISTORICAL_EDGE_LEFT_CELL_CLASS
          : PENDING_EDGE_LEFT_CELL_CLASS
        : historical
          ? HISTORICAL_EDGE_RIGHT_CELL_CLASS
          : PENDING_EDGE_RIGHT_CELL_CLASS,
    );
  }
};

/**
 * Traces the pending-requote outline down the column's outer edges (a no-op for
 * any other version), so the border wraps the whole carrier column rather than
 * every cell seam.
 */
/** A historical (actioned, superseded) version — the Restore target (ADR-031). */
const isHistoricalCarrier = (carrier: CarrierColumnGroup): boolean =>
  carrier.requoteReviewState === "actioned" &&
  carrier.isActiveVersion === false;

/**
 * Which quote this column holds. The single place the four states are told apart, so the
 * chip, the fill and the icon can never disagree about a column.
 *
 * Deleted is checked first: deleting is permitted only on the active version, so a deleted
 * group can also read as actioned-and-inactive, and the deletion is the fact worth showing.
 */
const quoteVersionState = (carrier: CarrierColumnGroup): QuoteVersionState => {
  if (carrier.isDeleted) return "deleted";
  if (carrier.requoteReviewState === "pending") return "pending";
  if (isHistoricalCarrier(carrier)) return "archived";

  return "active";
};

/**
 * A column that is not the carrier's current quote, and so carries a treatment of its own.
 *
 * Pending keeps the purple it has always had — it is waiting on the analyst. Archived and
 * deleted are grey: they are out of play, and sharing one colour with pending made it mean
 * both "decide this" and "this is over".
 */
const isStyledVersionCarrier = (carrier: CarrierColumnGroup): boolean =>
  quoteVersionState(carrier) !== "active";

/** The header fill for a column's state, or none for the carrier's current quote. */
const versionHeaderClasses = (carrier: CarrierColumnGroup): string[] => {
  const state = quoteVersionState(carrier);
  if (state === "active") return [];

  return [
    state === "pending"
      ? REQUOTE_PENDING_HEADER_CLASS
      : HISTORICAL_HEADER_CLASS,
  ];
};

const applyPendingRequoteOutline = (
  carrierGroup: ColGroupDef,
  carrier: CarrierColumnGroup,
): void => {
  if (!isStyledVersionCarrier(carrier)) return;
  const historical = quoteVersionState(carrier) !== "pending";
  tagVersionColumnEdge(carrierGroup, "left", historical);
  tagVersionColumnEdge(carrierGroup, "right", historical);
};

// ---------------------------------------------------------------------------
// Carrier-grouped columns — super-group wrapping BPG columns per carrier
// ---------------------------------------------------------------------------

/**
 * Wraps a carrier's BPG columns in a "Version" tier: "Current" for the
 * synthetic incumbent/requested column (no backing proposal), or a version
 * chip + received timestamp for every real proposal column, active or not.
 * Only used when `showVersionRow` is on. Always shown regardless of
 * ppx-quote-history — CD-1730 already shows every version as its own column,
 * so labeling which is which is useful independent of the deferred requote
 * review UX; the purple pending/historical treatment stays flag-gated.
 */
const buildVersionGroupDef = (
  carrier: CarrierColumnGroup,
  bpgColumns: (ColDef | ColGroupDef)[],
  quoteHistoryEnabled: boolean,
  isUnmappedCarrier: boolean,
): ColGroupDef => ({
  headerName: "",
  marryChildren: true,
  headerClass: [
    VERSION_HEADER_CLASS,
    ...(quoteHistoryEnabled && !isUnmappedCarrier
      ? versionHeaderClasses(carrier)
      : []),
  ],
  groupId: `version-${carrier.groupKey}`,
  headerGroupComponent: VersionRowHeader,
  headerGroupComponentParams: {
    // proposalId distinguishes the synthetic incumbent/requested baseline (no backing
    // proposal) from a real carrier column that simply has no quote yet — the two must
    // not render the same chip.
    proposalId: carrier.proposalId,
    quoteVersion: carrier.quoteVersion,
    receivedAt: carrier.receivedAt,
    versionState:
      quoteHistoryEnabled && !isUnmappedCarrier
        ? quoteVersionState(carrier)
        : "active",
  },
  children: bpgColumns,
});

/** Wraps a carrier's BPG columns in the Version tier when `showVersionRow` is on. */
const buildCarrierChildren = (
  carrier: CarrierColumnGroup,
  bpgColumns: (ColDef | ColGroupDef)[],
  showVersionRow: boolean,
  quoteHistoryEnabled: boolean,
  isUnmappedCarrier: boolean,
): (ColDef | ColGroupDef)[] =>
  showVersionRow
    ? [
        buildVersionGroupDef(
          carrier,
          bpgColumns,
          quoteHistoryEnabled,
          isUnmappedCarrier,
        ),
      ]
    : bpgColumns;

/**
 * Wraps each top-level column of a carrier lane in a "Source Document" cell, so
 * the row sits directly under Version in every lane and lines up with its label.
 * A single-group lane's top-level columns are its plans, so it gets one cell per
 * plan. A multi-group lane keeps its plan-group row, and a cell above that row
 * must span the group's plans, so it gets one cell per plan group. The cell is
 * blank for a lane with no source document, and over pad slots ("Not received")
 * that a document quoting fewer plans than requested left empty: the lane still
 * gets the row so every carrier's Source Document cells sit on the same header row.
 */
const wrapWithSourceDocument = (
  columns: (ColDef | ColGroupDef)[],
  documentName: string,
  laneKey: string,
  planById: Map<string, RfpBenefitPlan>,
): ColGroupDef[] =>
  columns.map((column, index) => {
    const holdsQuotedPlan = planIdsUnder(column).some(
      (planId) => !planById.get(planId)?.isPlaceholder,
    );
    const cellName = holdsQuotedPlan ? documentName : "";
    return {
      groupId: `source-document-${laneKey}-${index}`,
      headerName: cellName,
      headerTooltip: cellName || undefined,
      headerClass: column.headerClass,
      marryChildren: true,
      children: [column],
    };
  });

/** Ids of the plans a column spans, read from the plan context its builder attaches. */
const planIdsUnder = (column: ColDef | ColGroupDef): string[] => {
  const planId: unknown = column.context?.benefitPlanId;
  return [
    ...(typeof planId === "string" ? [planId] : []),
    ...("children" in column ? column.children.flatMap(planIdsUnder) : []),
  ];
};

/** Shared, carrier-invariant context threaded into {@link buildCarrierColumnGroup}. */
interface CarrierColumnGroupContext {
  coverage: RfpCoverage;
  currentCarrierId?: string;
  planDocumentMap?: PlanDocumentMap;
  movePlanMenuConfig?: MovePlanMenuConfig;
  placeholderMaterializeByPlanId?: Map<string, PlaceholderMaterializeTarget>;
  showVersionRow: boolean;
  padFillByPlanId?: Map<string, PadFillTarget>;
  quoteHistoryEnabled: boolean;
  networks: readonly string[];
  forcePlanOptionTier: boolean;
  getPlanMenuItems: GetMainMenuItems;
  strategy: ColumnBuildStrategy;
  nextFreePositionByPlanGroupId: Map<string, number>;
  copiedPlanCarrierId?: string;
  onPastePlan?: (proposalPlanGroupId: string, targetPosition: number) => void;
  isSingularPlan: boolean;
  showSourceDocumentRow: boolean;
  planById: Map<string, RfpBenefitPlan>;
}

/** Shared ingredients for a single lane's group column, computed once and reused by {@link buildCarrierColumnGroup}. */
interface LaneBuildResult {
  bpgColumns: (ColDef | ColGroupDef)[];
  isCurrentCarrier: boolean;
  isUnmappedCarrier: boolean;
  headerGroupComponentParams: Record<string, unknown>;
}

const buildLaneContent = (
  carrier: CarrierColumnGroup,
  ctx: CarrierColumnGroupContext,
): LaneBuildResult => {
  const isCurrentCarrier = carrier.carrierId === ctx.currentCarrierId;
  const isUnmappedCarrier = carrier.benefitPlanGroups.some(
    (group) => group.isUnmapped,
  );

  const leafBehaviorBuilder = ctx.strategy.getLeafColumnBehaviorBuilder();
  leafBehaviorBuilder.addParam("isCurrentCarrier", isCurrentCarrier);

  // Reuse existing per-BPG column builder. Menu items are attached at the
  // carrier header below, not per plan, so no callback is threaded through here.
  const planColumns = createBenefitPlanColumnsFromGroups(
    carrier.benefitPlanGroups,
    ctx.networks,
    leafBehaviorBuilder,
    ctx.planDocumentMap,
    isCurrentCarrier ? undefined : ctx.getPlanMenuItems,
    undefined,
    // Carrier-grouped (MPX) columns key colId off plan.id so colId === field —
    // required for popup-editor saves to resolve (see createPlanOptions… note).
    true,
    ctx.forcePlanOptionTier,
  );
  const bpgColumns = ctx.showSourceDocumentRow
    ? wrapWithSourceDocument(
        planColumns,
        carrier.documentName ?? "",
        carrier.groupKey,
        ctx.planById,
      )
    : planColumns;

  // The carrier's whole quote for this page is one ProposalPlanGroup, so
  // carrier-level actions (Move Plan Group) key off it rather than an individual
  // plan column. Eligible only when there's a real, current, non-placeholder
  // quote — never for historical versions, awaiting-quote placeholders, or
  // the synthetic Incumbent/Requested carrier. Independent of
  // ppx-quote-history/isUnmappedCarrier's pending-styling gate below: mapping
  // an unmapped group to a requested plan is the (older, separately-flagged)
  // btx-unmapped-plans capability, not part of deferred requote/quote-history.
  const movableGroup = carrier.benefitPlanGroups.find(
    (group) =>
      !group.isHistorical && !group.isPlaceholder && !group.isRequested,
  );
  const moveTarget = computeMoveTarget(
    movableGroup,
    ctx.movePlanMenuConfig,
    ctx.coverage.coverageType,
  );

  // Dismiss targets for this carrier's placeholder groups. The flattened grid
  // yields one single-plan BPG per plan, so several BPGs share one RFP group —
  // dedupe by rfpBenefitPlanGroupId so the group is dismissed once.
  const placeholderDismissTargets = collectDismissTargets(
    carrier,
    ctx.placeholderMaterializeByPlanId,
  );

  // Under-quoted groups with empty pad slots (CD-1612 partial fill): the
  // header offers a group-scoped dismissal, deduped by proposal plan group.
  const padDismissGroupIds = collectPadDismissGroupIds(
    carrier,
    ctx.padFillByPlanId,
  );

  // Plan groups this carrier's copied plan can be pasted into, offered from
  // the carrier header's menu rather than per-plan (see collectPasteTargets).
  const pasteTargets = collectPasteTargets(
    carrier,
    ctx.copiedPlanCarrierId,
    ctx.nextFreePositionByPlanGroupId,
  );

  const headerGroupComponentParams = buildCarrierHeaderParams(
    carrier,
    ctx.coverage,
    placeholderDismissTargets,
    padDismissGroupIds,
    moveTarget,
    ctx.movePlanMenuConfig,
    isUnmappedCarrier,
    ctx.quoteHistoryEnabled,
    pasteTargets,
    ctx.onPastePlan,
    ctx.isSingularPlan,
  );

  return {
    // The synthetic Incumbent/Requested carrier must stay locked at the
    // front of the table — AG Grid only respects suppressMovable on leaf
    // ColDefs, not on the ColGroupDef the carrier header renders as, so it
    // has to be set on every leaf underneath instead of on carrierGroup
    // itself (see buildCarrierColumnGroup).
    bpgColumns: isCurrentCarrier
      ? lockColumnsFromMoving(bpgColumns)
      : bpgColumns,
    isCurrentCarrier,
    isUnmappedCarrier,
    headerGroupComponentParams,
  };
};

/**
 * Recursively marks every leaf column non-movable. AG Grid only honors
 * `suppressMovable` on a `ColDef` (leaf), not on a `ColGroupDef` — a group
 * only "moves" because dragging its header drags all of its leaves together,
 * so locking the group means locking every leaf underneath it.
 */
const lockColumnsFromMoving = (
  columns: (ColDef | ColGroupDef)[],
): (ColDef | ColGroupDef)[] =>
  columns.map((column) =>
    "children" in column
      ? { ...column, children: lockColumnsFromMoving(column.children) }
      : { ...column, suppressMovable: true },
  );

const buildCarrierHeaderClasses = (
  carrier: CarrierColumnGroup,
  ctx: CarrierColumnGroupContext,
  isUnmappedCarrier: boolean,
): string[] => [
  CARRIER_HEADER_CLASS,
  ...(isUnmappedCarrier ? [UNMAPPED_CARRIER_HEADER_CLASS] : []),
  // An unmapped group never has its own plan-group-level state, so it
  // falls back to its owning proposal's — which can be pending/historical
  // even though the group itself has no requested plan to promote into.
  // The yellow unmapped treatment always wins over the version treatments.
  ...(ctx.quoteHistoryEnabled && !isUnmappedCarrier
    ? versionHeaderClasses(carrier)
    : []),
];

/**
 * Builds one version's group column: Carrier tier (header/menu) → Version tier → BPG.
 * `laneIndex` is the version's position among its carrier's versions; it is
 * tagged on the group so a drag can regroup the carrier's versions (see gatherCarrierLanes).
 */
const buildCarrierColumnGroup = (
  carrier: CarrierColumnGroup,
  ctx: CarrierColumnGroupContext,
  laneIndex?: number,
): ColGroupDef => {
  const {
    bpgColumns,
    isCurrentCarrier,
    isUnmappedCarrier,
    headerGroupComponentParams,
  } = buildLaneContent(carrier, ctx);

  const carrierGroup: ColGroupDef = {
    headerName: carrier.carrierName,
    marryChildren: true,
    headerClass: buildCarrierHeaderClasses(carrier, ctx, isUnmappedCarrier),
    groupId: `carrier-${carrier.groupKey}`,
    ...(laneIndex !== undefined && {
      context: {
        carrierId: carrier.carrierId,
        laneIndex,
      } satisfies CarrierLaneContext,
    }),
    ...(!isCurrentCarrier && {
      headerGroupComponent: CarrierGroupHeader,
      headerGroupComponentParams,
    }),
    children: buildCarrierChildren(
      carrier,
      bpgColumns,
      ctx.showVersionRow,
      ctx.quoteHistoryEnabled,
      isUnmappedCarrier,
    ),
  };

  if (ctx.quoteHistoryEnabled && !isUnmappedCarrier) {
    applyPendingRequoteOutline(carrierGroup, carrier);
  }

  return carrierGroup;
};

/**
 * True for the synthetic Incumbent/Requested carrier once it bundles more than
 * one real requested plan group (Unmapped Plans page: one reference entry per
 * real requested group). Unlike a real carrier's own multi-group quote (e.g.
 * alternates, which share one proposal and belong under one Carrier/Version
 * banner), these groups have no natural single-quote identity tying them
 * together, so each needs its own independent Carrier/Version header box.
 */
const isSplitRequestedCarrier = (carrier: CarrierColumnGroup): boolean =>
  carrier.benefitPlanGroups.length > 1 &&
  carrier.benefitPlanGroups.every((group) => group.isRequested);

/**
 * Splits the synthetic Incumbent/Requested carrier into one mini-carrier per
 * plan group, each rendering as its own Carrier/Version/BPG column so
 * unrelated requested groups don't share one header banner. groupKey keeps
 * the colon-separated `<carrierId>:<suffix>` shape (ADR-024's document-origin
 * lanes use the same shape) so extractCarrierOrderFromEvent still resolves
 * the real carrierId back out of the group id for drag reordering — this
 * carrier is locked/undraggable, but the id must still parse cleanly.
 * The group's own label is folded into carrierName since a lone plan group
 * drops its own header row (createBenefitPlanColumnsFromGroups) — without it,
 * splitting would lose the distinguishing name entirely.
 *
 * A `current` group's own resolved label (its carrier name, or plain
 * "Current" when none is known) usually distinguishes it from siblings, so it
 * needs no suffix — but the RFP schema allows several `current` groups at
 * different positions (`src/apps/backend/btx/internal/ent/schema/benefitplangroup.go:84-92`),
 * and when two resolve to the same label (both unnamed, or coincidentally the
 * same carrier), that label alone no longer tells the columns apart. Those
 * groups fall back to their nickname (or a "Group N" position if they don't
 * have one) instead of the usual policy-period suffix, which would only
 * restate the "Current" the label already carries. `newBusiness`/`alternate`
 * groups all collapse to the same "Requested" base regardless of carrier, so
 * they always keep the policy-period suffix (e.g. "Requested — Alternate 2").
 */
const splitRequestedCarrierByGroup = (
  carrier: CarrierColumnGroup,
): CarrierColumnGroup[] => {
  const labelFor = (group: RequestedBenefitPlanGroup): string =>
    formatRequestedColumnLabel(group.currentCarrierName, group.policyPeriod);

  const currentLabelCounts = new Map<string, number>();
  for (const group of carrier.benefitPlanGroups) {
    if (group.policyPeriod !== BaseBenefitPlanPolicyPeriod.current) {
      continue;
    }
    const label = labelFor(group);
    currentLabelCounts.set(label, (currentLabelCounts.get(label) ?? 0) + 1);
  }

  return carrier.benefitPlanGroups.map((group, index) => {
    const baseLabel = labelFor(group);
    const isCurrentGroup =
      group.policyPeriod === BaseBenefitPlanPolicyPeriod.current;
    const isUniqueCurrentLabel =
      isCurrentGroup && currentLabelCounts.get(baseLabel) === 1;

    let carrierName = baseLabel;
    if (!isUniqueCurrentLabel) {
      carrierName = isCurrentGroup
        ? `${baseLabel} — ${group.nickname ?? `Group ${index + 1}`}`
        : `${baseLabel} — ${formatPolicyPeriodDisplay(group.policyPeriod, group.nickname)}`;
    }

    return {
      ...carrier,
      groupKey: `${carrier.groupKey}:${group.id}`,
      carrierName,
      benefitPlanGroups: [group],
    };
  });
};

/**
 * Builds column definitions with a carrier-level super-group above BPG groups.
 *
 * Column hierarchy: Carrier → Version (when `showVersionRow`) → Source Document
 * (when any column came from an AI-processed document) → BPG (only when a
 * carrier holds several groups) → Plan → Plan Option → Network (leaf columns).
 * See wrapWithSourceDocument for how Source Document cells split per plan or
 * per plan group.
 *
 * The Benefits (row label) column is added once at the left. Each carrier
 * gets a `ColGroupDef` whose children are the BPG columns produced by
 * `createBenefitPlanColumnsFromGroups`.
 *
 * @param carrierGroups - Per-carrier BPG data from the adapter
 * @param coverage - Merged coverage with all plans across carriers (for network detection + row data)
 * @param disableNetworkColumns - Whether to disable network sub-columns
 * @param strategy - Column building strategy to determine grouping and column behavior
 * @returns AG Grid column definition array
 */
export const buildCarrierGroupedColumns = (
  carrierGroups: CarrierColumnGroup[],
  coverage: RfpCoverage,
  disableNetworkColumns = false,
  strategy: ColumnBuildStrategy,
  currentCarrierId?: string,
  planDocumentMap?: PlanDocumentMap,
  movePlanMenuConfig?: MovePlanMenuConfig,
  carrierGroupedBenefitsHeaderLabels?: string[],
  placeholderMaterializeByPlanId?: Map<string, PlaceholderMaterializeTarget>,
  showVersionRow = false,
  padFillByPlanId?: Map<string, PadFillTarget>,
  onCopyPlan?: (details: CopiedPlanDetails, id: string) => void,
  onPastePlan?: (proposalPlanGroupId: string, targetPosition: number) => void,
  copiedPlanCarrierId?: string,
  openDeletePlanDialog?: (data: DeletePlanInfo) => void,
  /**
   * ppx-quote-history: when false, requote/quote-history is deferred — every
   * carrier version still renders as its own column (CD-1730), but with no
   * pending/historical styling and no Replace/Disregard/Restore/Move Plan Group
   * actions. Defaults to true so existing callers keep today's behavior.
   */
  quoteHistoryEnabled = true,
): (ColDef | ColGroupDef)[] => {
  if (!carrierGroups || carrierGroups.length === 0) {
    return [];
  }

  if (!coverage?.benefitPlans || !Array.isArray(coverage.benefitPlans)) {
    return [];
  }

  const columnDefs: (ColDef | ColGroupDef)[] = [];

  // Decide the header tiers ONCE for the whole grid, not per carrier. AG Grid
  // balances column trees of unequal depth by padding blank header rows above
  // the shallower groups, so any carrier whose subtree comes out shallower has
  // its name pushed below the Carrier row. Per-carrier getNetworks keys off the
  // carrier's first plan group's product type, which diverges across carriers
  // now that a dental coverage mixes hmo/ppo/alternate groups — one grid-wide
  // decision keeps every carrier subtree the same depth.
  const networks = disableNetworkColumns
    ? []
    : getGridNetworks(carrierGroups, coverage);

  // Same reasoning for the plan-option tier when there are no networks: a
  // multi-plan column (e.g. Incumbent/Requested) is 2-tier while single-plan
  // carrier columns are 1-tier, which staircases the same way.
  const forcePlanOptionTier =
    networks.length === 0 &&
    carrierGroups.some((c) =>
      c.benefitPlanGroups.some((g) => (g.benefitPlans?.length ?? 0) > 1),
    );

  // carrierGroups only carries BenefitPlanSummary (no benefits/plan-level
  // fields) — the full plan data lives on coverage.benefitPlans.
  const planById = new Map<string, RfpBenefitPlan>(
    (coverage.benefitPlans ?? [])
      .filter((plan): plan is RfpBenefitPlan & { id: string } => !!plan.id)
      .map((plan) => [plan.id, plan]),
  );

  const carrierIdByPlanGroupId = new Map<string, string>(
    carrierGroups.flatMap((carrier) =>
      carrier.benefitPlanGroups.map((group): [string, string] => [
        group.proposalPlanGroupId ?? group.id,
        carrier.carrierId,
      ]),
    ),
  );

  const carrierNameByCarrierId = new Map<string, string>(
    carrierGroups.map((carrier) => [carrier.carrierId, carrier.carrierName]),
  );

  // Delete Plan Group hides when there's only one real proposal plan in the
  // whole coverage (excluding the synthetic Incumbent/Requested group and
  // placeholders) — counted by distinct plan group, not by the plan/tier
  // columns nested inside it, so a single proposal with multiple option tiers
  // still counts as one plan.
  const isSingularPlan =
    new Set(
      carrierGroups
        .flatMap((carrier) => carrier.benefitPlanGroups)
        .filter((group) => !group.isPlaceholder && !group.isRequested)
        .map(({ proposalPlanGroupId, id }) => proposalPlanGroupId ?? id),
    ).size <= 1;

  // Plan groups whose owning carrier column is a non-active version (a pending
  // requote or a historical version) — read-only in Quote History. The delete
  // endpoint 400s on these (guardDeletableOnGroupVersion), so "Delete Plan"
  // is suppressed for them below. Keyed the same way as carrierIdByPlanGroupId.
  // An unmapped group answers no requested plan, so it has no version of its
  // own to protect — carrier.isActiveVersion is only its owning proposal's
  // state, decided by a requote on a sibling requested plan of that proposal,
  // which has no bearing on this group (mirrors guardDeletableOnGroupVersion's
  // own unmapped carve-out). With quote history off there is no Quote History
  // for a version to be read-only in: every version is an ordinary board
  // column and the endpoint accepts the delete, so nothing is suppressed.
  const readOnlyPlanGroupIds = new Set<string>(
    quoteHistoryEnabled
      ? carrierGroups
          .filter((carrier) => carrier.isActiveVersion === false)
          .flatMap((carrier) =>
            carrier.benefitPlanGroups
              .filter((group) => !group.isUnmapped)
              .map((group) => group.proposalPlanGroupId ?? group.id),
          )
      : [],
  );

  // An uploaded document's unmapped plans each arrive as their own size-1 group yet stack
  // into one column (btx-unmapped-plans), so keying off group.size alone hides
  // Delete on a column that visibly holds several plans.
  const lanePlanCountByPlanGroupId =
    buildLanePlanCountByPlanGroupId(carrierGroups);

  // Paste targets the lowest slot not held by a real plan — a gap or a
  // placeholder pad both qualify, since pasting into a pad fills it. A plain
  // count (size, real-plan count) can't find that slot once positions have
  // gaps, so derive it from the actual real-plan positions.
  const nextFreePositionByPlanGroupId = buildNextFreePositionByPlanGroupId(
    carrierGroups,
    planById,
  );

  // Only AI-processed (document-origin) columns have a source document, so the
  // row appears only on a page that holds at least one of them.
  const showSourceDocumentRow = carrierGroups.some(
    (carrier) => !!carrier.documentName,
  );

  const getPlanMenuItems = buildPlanMenuItems(
    planById,
    carrierIdByPlanGroupId,
    carrierNameByCarrierId,
    lanePlanCountByPlanGroupId,
    onCopyPlan,
    openDeletePlanDialog,
    placeholderMaterializeByPlanId,
    padFillByPlanId,
    readOnlyPlanGroupIds,
  );

  const carrierColumnGroupCtx: CarrierColumnGroupContext = {
    coverage,
    currentCarrierId,
    planDocumentMap,
    movePlanMenuConfig,
    placeholderMaterializeByPlanId,
    showVersionRow,
    padFillByPlanId,
    quoteHistoryEnabled,
    networks,
    forcePlanOptionTier,
    getPlanMenuItems,
    nextFreePositionByPlanGroupId,
    copiedPlanCarrierId,
    onPastePlan,
    strategy,
    isSingularPlan,
    showSourceDocumentRow,
    planById,
  };

  const carriersWithGroups = carrierGroups.filter(
    (carrier) => carrier.benefitPlanGroups.length > 0,
  );
  const carrierGroupDefs: ColGroupDef[] = carriersWithGroups.flatMap(
    (carrier, idx) => {
      if (isSplitRequestedCarrier(carrier)) {
        // Locked, undraggable pieces: no lane context, so a drag never regroups them.
        return splitRequestedCarrierByGroup(carrier).map((piece) =>
          buildCarrierColumnGroup(piece, carrierColumnGroupCtx),
        );
      }

      const laneIndex = carriersWithGroups
        .slice(0, idx)
        .filter(
          (earlier) =>
            earlier.carrierId === carrier.carrierId &&
            !isSplitRequestedCarrier(earlier),
        ).length;
      return [
        buildCarrierColumnGroup(carrier, carrierColumnGroupCtx, laneIndex),
      ];
    },
  );

  // Static benefits label column (with labelled tiers when doc map present).
  // The Source Document row sits directly above each plan's own tier, so its
  // label goes directly above the last label, which names that tier.
  const benefitsHeaderLabels =
    showSourceDocumentRow && carrierGroupedBenefitsHeaderLabels?.length
      ? [
          ...carrierGroupedBenefitsHeaderLabels.slice(0, -1),
          SOURCE_DOCUMENT_LABEL,
          ...carrierGroupedBenefitsHeaderLabels.slice(-1),
        ]
      : carrierGroupedBenefitsHeaderLabels;
  columnDefs.push(
    benefitsHeaderLabels?.length
      ? createBenefitsColumnWithHeaderLabels(benefitsHeaderLabels)
      : createLabelledBenefitsColumn(planDocumentMap, networks.length > 0),
  );

  columnDefs.push(...carrierGroupDefs);

  return columnDefs;
};

/**
 * Builds header group component params for a carrier. Extracted to reduce
 * complexity of buildCarrierGroupedColumns.
 */
const buildCarrierHeaderParams = (
  carrier: CarrierColumnGroup,
  coverage: RfpCoverage,
  placeholderDismissTargets: PlaceholderMaterializeTarget[],
  padDismissGroupIds: string[],
  moveTarget: MovePlanTarget | undefined,
  movePlanMenuConfig: MovePlanMenuConfig | undefined,
  isUnmapped: boolean,
  quoteHistoryEnabled: boolean,
  pasteTargets: PasteTarget[],
  onPastePlan:
    | ((proposalPlanGroupId: string, targetPosition: number) => void)
    | undefined,
  isSingularPlan: boolean,
): Record<string, unknown> => ({
  carrierName: carrier.carrierName,
  planGroupIds: [
    ...new Set(
      carrier.benefitPlanGroups
        .filter(({ isPlaceholder }) => !isPlaceholder)
        .map(({ proposalPlanGroupId, id }) => proposalPlanGroupId ?? id),
    ),
  ],
  placeholderDismissTargets,
  padDismissGroupIds,
  rfpId: coverage.rfpId,
  proposalId: carrier.proposalId,
  quoteVersion: carrier.quoteVersion,
  // ppx-quote-history off: requote/quote-history is deferred, so a non-active
  // version renders as its own plain column (CD-1730 — every carrier version
  // still shows) with none of the pending/historical treatment or actions below.
  // An unmapped group never has its own plan-group-level state and falls back
  // to its owning proposal's, which can be pending/historical even though the
  // group itself has no requested plan to Replace/Disregard/Restore into — so
  // isUnmapped always wins, regardless of the flag.
  isPendingRequote:
    quoteHistoryEnabled &&
    !isUnmapped &&
    carrier.requoteReviewState === "pending",
  isHistorical:
    quoteHistoryEnabled &&
    !isUnmapped &&
    (isHistoricalCarrier(carrier) || !!carrier.isDeleted),
  // A deleted column is read-only for this release: Replace would have to restore the
  // group as well as promote it, which the API does not do yet, so the menu offers nothing
  // rather than an action that would fail.
  isDeleted: quoteHistoryEnabled && !isUnmapped && !!carrier.isDeleted,
  // Resolved once here so the chip, the header fill and the icon cannot disagree. Gated
  // like the flags above: with quote history off, or on an unmapped column, every version
  // reads as the carrier's current quote.
  versionState:
    quoteHistoryEnabled && !isUnmapped ? quoteVersionState(carrier) : "active",
  isUnmapped,
  // The singular guard keeps a requested plan-comparison page from being emptied of its
  // only quote. The Unmapped Plans page is a triage queue, not a comparison: clearing its
  // last quote retires the page, which is the outcome the analyst is after.
  isSingularPlan: isSingularPlan && !isUnmapped,
  // Independent of ppx-quote-history/isUnmapped above: mapping an unmapped
  // group to a requested plan is the (older, separately-flagged)
  // btx-unmapped-plans capability, not part of deferred requote/quote-history.
  ...(moveTarget &&
    movePlanMenuConfig && {
      moveTarget,
      onMovePlan: movePlanMenuConfig.onMovePlan,
    }),
  ...(pasteTargets.length > 0 && onPastePlan && { pasteTargets, onPastePlan }),
});

const buildLanePlanCountByPlanGroupId = (
  carrierGroups: CarrierColumnGroup[],
): Map<string, number> =>
  new Map(
    carrierGroups.flatMap((carrier) => {
      const laneCount = carrier.benefitPlanGroups.reduce(
        (sum, group) => sum + (group.benefitPlans?.length ?? 0),
        0,
      );
      return carrier.benefitPlanGroups.map((group): [string, number] => [
        group.proposalPlanGroupId ?? group.id,
        laneCount,
      ]);
    }),
  );

// A placeholder/pad slot is meant to BE the paste target — filling it makes
// it disappear — so it must not count as "occupied." Only real, persisted
// plans block a slot; a gap with no plan at all (real or placeholder) is
// exactly as free as a gap that happens to have a pad in it.
const buildNextFreePositionByPlanGroupId = (
  carrierGroups: CarrierColumnGroup[],
  planById: Map<string, RfpBenefitPlan>,
): Map<string, number> =>
  new Map(
    carrierGroups.flatMap((carrier) =>
      carrier.benefitPlanGroups.map((group): [string, number] => {
        const occupied = new Set(
          (group.benefitPlans ?? [])
            .filter((plan) => !planById.get(plan.id)?.isPlaceholder)
            .map((plan) => plan.position)
            .filter(
              (planPosition): planPosition is number => planPosition != null,
            ),
        );
        let candidate = 1;
        while (occupied.has(candidate)) candidate++;
        return [group.proposalPlanGroupId ?? group.id, candidate];
      }),
    ),
  );

// AG Grid reports it via `columnGroup`, not `column` — `column` is only populated
// for a leaf column click (see GetMainMenuItemsParams).
const buildPlanMenuItems = (
  planById: Map<string, RfpBenefitPlan>,
  carrierIdByPlanGroupId: Map<string, string>,
  carrierNameByCarrierId: Map<string, string>,
  // Total plans in the owning carrier column, keyed by proposal plan group —
  // the gate for Delete Plan (see buildLanePlanCountByPlanGroupId).
  lanePlanCountByPlanGroupId: Map<string, number>,
  onCopyPlan?: (details: CopiedPlanDetails, id: string) => void,
  openDeletePlanDialog?: (data: DeletePlanInfo) => void,
  placeholderMaterializeByPlanId?: Map<string, PlaceholderMaterializeTarget>,
  padFillByPlanId?: Map<string, PadFillTarget>,
  // Plan groups on a non-active proposal version (a pending requote or a
  // historical version) are read-only in Quote History — the delete endpoint
  // rejects them (guardDeletableOnProposalVersion, 400). Suppress "Delete Plan"
  // there rather than surfacing an action that always fails.
  readOnlyPlanGroupIds?: Set<string>,
): GetMainMenuItems => {
  const getColumnInfo = (
    params: Parameters<GetMainMenuItems>[0],
  ): {
    plan?: RfpBenefitPlan;
    planGroupId?: string;
    carrierId?: string;
    size: number;
    laneSize: number;
    groupHeader?: string;
  } => {
    const context = params.column
      ? extractColumnContext(params.column)
      : (params.columnGroup?.getColGroupDef()?.context as
          | ReturnType<typeof extractColumnContext>
          | undefined);
    const plan = context?.benefitPlanId
      ? planById.get(context.benefitPlanId)
      : undefined;
    const planGroupId = context?.planGroupId;
    const carrierId = planGroupId
      ? carrierIdByPlanGroupId.get(planGroupId)
      : undefined;
    const size = context?.size ?? 0;
    const laneSize = planGroupId
      ? (lanePlanCountByPlanGroupId.get(planGroupId) ?? size)
      : size;

    return {
      plan,
      planGroupId,
      carrierId,
      size,
      laneSize,
      groupHeader: context?.identifier,
    };
  };

  return (params) => {
    const columnInfo = getColumnInfo(params);

    if (!columnInfo?.plan || !columnInfo?.planGroupId || !columnInfo.carrierId)
      return [];
    const { plan, planGroupId, carrierId, laneSize, groupHeader } = columnInfo;
    const planLabel = groupHeader ?? plan?.identifier ?? "";

    const items: (DefaultMenuItem | MenuItemDef)[] = [
      {
        name: "Copy Plan",
        icon: COPY_PLAN_ICON,
        action: () => {
          onCopyPlan?.(
            {
              carrierId,
              carrierName: carrierNameByCarrierId.get(carrierId) ?? "",
              planLabel,
              sourcePlanId: plan.id ?? "",
              type: plan.type,
              optionName: plan.optionName,
              benefits: plan.benefits,
              fundingType: plan.fundingType,
              participationRequirements: plan.participationRequirements,
              rateGuarantee: plan.rateGuarantee,
              rateCap: plan.rateCap,
              eligibilityClasses: plan.eligibilityClasses,
            },
            planLabel,
          );
        },
      },
    ];

    if (laneSize > 1 && !readOnlyPlanGroupIds?.has(planGroupId)) {
      items.push({
        name: "Delete Plan",
        icon: DELETE_PLAN_ICON,
        cssClasses: ["ag-menu-option-delete-plan"],
        action: () => {
          const groupName = carrierNameByCarrierId.get(carrierId) ?? "";

          // Same dispatch order as the edit/save path (useProposalPlanBenefitsMutation):
          // a pad slot and a placeholder column are both synthesized at read time and
          // have no proposal_plan row, so neither can hit the plan delete endpoint.
          const padTarget = plan.id ? padFillByPlanId?.get(plan.id) : undefined;
          const placeholderTarget = plan.id
            ? placeholderMaterializeByPlanId?.get(plan.id)
            : undefined;

          if (padTarget) {
            openDeletePlanDialog?.({
              kind: "pad",
              name: planLabel,
              groupName,
              target: padTarget,
            });
          } else if (placeholderTarget) {
            openDeletePlanDialog?.({
              kind: "placeholder",
              name: planLabel,
              groupName,
              target: placeholderTarget,
            });
          } else if (plan.id) {
            openDeletePlanDialog?.({
              kind: "real",
              name: planLabel,
              groupName,
              id: plan.id,
            });
          }
        },
      });
    }

    return items;
  };
};

const collectPasteTargets = (
  carrier: CarrierColumnGroup,
  copiedPlanCarrierId: string | undefined,
  nextFreePositionByPlanGroupId: Map<string, number>,
): PasteTarget[] => {
  if (!copiedPlanCarrierId || copiedPlanCarrierId !== carrier.carrierId) {
    return [];
  }

  const baseGroup = carrier.benefitPlanGroups.find(
    (group) => !group.isPlaceholder,
  );
  if (!baseGroup) return [];

  const planGroupId = baseGroup.proposalPlanGroupId ?? baseGroup.id;
  return [
    {
      planGroupId,
      targetPosition: nextFreePositionByPlanGroupId.get(planGroupId) ?? 1,
    },
  ];
};

// computeMoveTarget resolves the "Move Plan Group" target for a carrier's movable group,
// gated on the coverage offering a page the group is allowed to land on.
const computeMoveTarget = (
  movableGroup: BenefitPlanGroup | undefined,
  movePlanMenuConfig: MovePlanMenuConfig | undefined,
  coverageType: string,
): MovePlanTarget | undefined => {
  if (!movableGroup || !movePlanMenuConfig) return undefined;

  const destinations = movePlanDestinations(
    movableGroup.productType,
    movePlanMenuConfig.requestedPages,
    coverageType,
    movePlanMenuConfig.currentPageId,
  );
  if (destinations.length === 0) return undefined;

  return {
    groupId: movableGroup.proposalPlanGroupId ?? movableGroup.id,
    productType: movableGroup.productType,
  };
};

// collectPadDismissGroupIds returns the proposal plan group ids of the carrier's
// real groups that carry empty pad slots (CD-1612 partial fill), deduped —
// dismissal (padSuppressed) is group-scoped.
const collectPadDismissGroupIds = (
  carrier: CarrierColumnGroup,
  padFillByPlanId: Map<string, PadFillTarget> | undefined,
): string[] => {
  if (!padFillByPlanId) return [];

  const groupIds = carrier.benefitPlanGroups
    .filter((group) => !group.isPlaceholder)
    .flatMap((group) => group.benefitPlans ?? [])
    .map((plan) => padFillByPlanId.get(plan.id)?.proposalPlanGroupId)
    .filter((id): id is string => id != null);

  return [...new Set(groupIds)];
};

// collectDismissTargets returns the dedup-by-RFP-group materialize targets for a
// carrier's placeholder groups. The flattened grid produces one single-plan BPG
// per plan (BPG.id === plan.id), so it keys off the plan id into the map and keeps
// one target per rfpBenefitPlanGroupId (dismissal materializes the whole group).
const collectDismissTargets = (
  carrier: CarrierColumnGroup,
  placeholderMaterializeByPlanId:
    | Map<string, PlaceholderMaterializeTarget>
    | undefined,
): PlaceholderMaterializeTarget[] => {
  if (!placeholderMaterializeByPlanId) return [];

  const byGroup = new Map<string, PlaceholderMaterializeTarget>();
  for (const group of carrier.benefitPlanGroups) {
    if (!group.isPlaceholder) continue;

    const planId = group.benefitPlans?.[0]?.id;
    const target = planId
      ? placeholderMaterializeByPlanId.get(planId)
      : undefined;
    if (target && !byGroup.has(target.rfpBenefitPlanGroupId)) {
      byGroup.set(target.rfpBenefitPlanGroupId, target);
    }
  }

  return [...byGroup.values()];
};
